"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import NavBarSecond from "../components/NavBar/NavBarSecond";
import ProductOrbitHero from "../components/Products/ProductOrbitHero";
import ProductsShowcase, {
  type ShowcaseProduct,
} from "../components/Products/ProductsShowcase";

const products: ShowcaseProduct[] = [
  {
    id: "speechsync",
    title: "SpeechSync AAC",
    tagline: "Breaking Barriers in Communication",
    description:
      "An advanced Augmentative and Alternative Communication app that helps specially-abled children express themselves clearly and comfortably, available on the App Store and Google Play.",
    category: "Mobile App",
    image: "/products/tab-hero.webp",
    imageAlt: "SpeechSync AAC app on tablet",
    href: "/products/speechsync",
    features: ["AAC Communication", "Mobile App", "App Store & Google Play"],
    tone: "light",
  },
  {
    id: "speeching-cards",
    title: "Speeching Cards",
    tagline: "Interactive Learning Experience",
    description:
      "A fun flash card series that boosts communication with What?, Where?, When?, and Who? prompts, plus Sequencing and Yes/No cards for language and social skills.",
    category: "Learning Cards",
    image: "/speechingcards.png",
    imageAlt: "Speeching Cards learning set",
    href: "/products/speeching-cards",
    features: ["Interactive Learning", "Flash Cards", "Communication Skills"],
    tone: "blush",
  },
  {
    id: "sogpod",
    title: "SOG Pod",
    tagline: "Feel. Regulate. Thrive.",
    description:
      'Sensory On the Go Pod is a fully-equipped mobile sensory room that brings calming, regulating, and stimulating experiences to children at schools, clinics, events, and homes.',
    category: "Device",
    image: "/SOGpod.svg",
    imageAlt: "SOG Pod multi-sensory device",
    href: "/products/sogpod",
    features: ["Multi-sensory Pod", "Emotional Regulation", "Therapy Support"],
    tone: "gradient",
  },
  {
    id: "neurocular",
    title: "Neurocular",
    tagline: "Play. Learn. Grow.",
    description:
      "A playful visual development game app that turns therapy into adventure, building tracking, focus, coordination, visual perception, and spatial skills through interactive play.",
    category: "Game App",
    image: "/products/neurocular/fingertrack.png",
    imageAlt: "Neurocular visual development game",
    href: "/products/neurocular",
    features: ["Vision Training", "Interactive Games", "Visual Skills"],
    tone: "sky",
    cover: true,
  },
];

export default function ProductsPageContent() {
  const reduceMotion = useReducedMotion();

  return (
    <div className="min-h-screen bg-white">
      <header className="absolute top-0 w-full z-50">
        <NavBarSecond />
      </header>

      <ProductOrbitHero
        description="Explore our suite of products designed to support therapy, improve learning, and help children grow."
      />

      <ProductsShowcase products={products} />

      <motion.section
        initial={reduceMotion ? false : { opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.55 }}
        className="relative overflow-hidden bg-gradient-to-r from-[#54169C] to-[#DA159B] py-16 md:py-20"
      >
        <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden>
          <div className="absolute -right-10 top-0 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute -left-10 bottom-0 w-72 h-72 rounded-full bg-[#FFA9E7]/30 blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-white text-3xl md:text-4xl mb-4"
            style={{ fontFamily: "HuluStyle", fontWeight: 600 }}
          >
            Need help choosing the right tool?
          </h2>
          <p
            className="text-white/85 text-base md:text-lg mb-8"
            style={{ fontFamily: "HuluStyle", fontWeight: 400 }}
          >
            Our team can guide you to the product that best supports your child&apos;s goals.
          </p>
          <Link
            href="/contact-us"
            className="inline-flex items-center gap-2 bg-white text-[#54169C] py-2.5 px-8 rounded-[40px] hover:bg-white/90 transition duration-300"
            style={{ fontFamily: "HuluStyle", fontWeight: 500 }}
          >
            Contact us
          </Link>
        </div>
      </motion.section>
    </div>
  );
}
